import { Loader2, MapPin, Navigation, Search, X } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { typography } from '../../styles/typography';
import { PrimaryButton } from '../ui';

// Google Maps API key (same as dashboard)
const GOOGLE_MAPS_API_KEY = 'AIzaSyA2tidsBVB4Kn3Ev4hf84hCsbSCSjFXKsI';

const HaltPicker = ({ isOpen, onClose, onConfirm, initialLocation = null, initialAddress = null }) => {
  const mapRef = useRef(null);
  const googleMapInstanceRef = useRef(null);
  const geocoderRef = useRef(null);
  const autocompleteServiceRef = useRef(null);
  const placesServiceRef = useRef(null);

  const [isGoogleMapsLoaded, setIsGoogleMapsLoaded] = useState(false);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [isLoadingAddress, setIsLoadingAddress] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(
    initialLocation || { lat: 23.72463575806444, lng: 78.47606996147735 }
  );
  const [currentZoom, setCurrentZoom] = useState(15);
  const [currentAddress, setCurrentAddress] = useState(initialAddress || '');
  
  // Search functionality
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef(null);

  // Function to verify Google Maps API is fully loaded
  const verifyGoogleMapsLoaded = useCallback(() => {
    return (
      window.google &&
      window.google.maps &&
      window.google.maps.MapTypeId &&
      window.google.maps.ControlPosition &&
      window.google.maps.Geocoder &&
      window.google.maps.places
    );
  }, []);

  // Load Google Maps script
  const loadGoogleMapsScript = useCallback((callback) => {
    if (verifyGoogleMapsLoaded()) {
      setIsGoogleMapsLoaded(true);
      callback();
      return;
    }

    if (document.querySelector(`script[src*="maps.googleapis.com/maps/api/js"]`)) {
      const checkGoogleMaps = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(checkGoogleMaps);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 100);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places,geometry&loading=async`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      const verifyInterval = setInterval(() => {
        if (verifyGoogleMapsLoaded()) {
          clearInterval(verifyInterval);
          setIsGoogleMapsLoaded(true);
          callback();
        }
      }, 50);

      setTimeout(() => {
        clearInterval(verifyInterval);
        if (verifyGoogleMapsLoaded()) {
          setIsGoogleMapsLoaded(true);
          callback();
        } else {
          console.error('Google Maps API did not fully initialize');
        }
      }, 5000);
    };
    script.onerror = (e) => {
      console.error('Failed to load Google Maps script.', e);
    };
    document.head.appendChild(script);
  }, [verifyGoogleMapsLoaded]);

  // Get current location
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setIsLoadingLocation(false);
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setSelectedLocation(location);
        setCurrentZoom(15);
        if (googleMapInstanceRef.current) {
          googleMapInstanceRef.current.panTo(
            new window.google.maps.LatLng(location.lat, location.lng)
          );
          googleMapInstanceRef.current.setZoom(15);
        }
        setIsLoadingLocation(false);
        reverseGeocodeLocation(location);
      },
      (error) => {
        console.error('Error getting current location:', error);
        setIsLoadingLocation(false);
      }
    );
  }, []);

  // Reverse geocode location to get address
  const reverseGeocodeLocation = useCallback((location) => {
    if (!geocoderRef.current || isLoadingAddress) return;

    setIsLoadingAddress(true);
    const geocoder = geocoderRef.current;
    
    geocoder.geocode(
      { location: new window.google.maps.LatLng(location.lat, location.lng) },
      (results, status) => {
        setIsLoadingAddress(false);
        if (status === 'OK' && results && results.length > 0) {
          setCurrentAddress(results[0].formatted_address);
        } else {
          setCurrentAddress('');
        }
      }
    );
  }, [isLoadingAddress]);

  // Initialize map
  useEffect(() => {
    if (!isOpen) {
      // Clean up when closed
      googleMapInstanceRef.current = null;
      return;
    }

    loadGoogleMapsScript(() => {
      if (mapRef.current && !googleMapInstanceRef.current) {
        const mapOptions = {
          center: new window.google.maps.LatLng(selectedLocation.lat, selectedLocation.lng),
          zoom: currentZoom,
          mapTypeId: window.google.maps.MapTypeId.ROADMAP,
          zoomControl: true,
          zoomControlOptions: {
            position: window.google.maps.ControlPosition.RIGHT_TOP,
          },
          fullscreenControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          myLocationControl: false,
        };

        const map = new window.google.maps.Map(mapRef.current, mapOptions);
        googleMapInstanceRef.current = map;
        geocoderRef.current = new window.google.maps.Geocoder();
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        placesServiceRef.current = new window.google.maps.places.PlacesService(map);

        // Trigger resize to ensure map renders properly
        setTimeout(() => {
          if (window.google && window.google.maps && window.google.maps.event) {
            window.google.maps.event.trigger(map, 'resize');
            // Get initial address for default location
            reverseGeocodeLocation(selectedLocation);
          }
        }, 100);

        // Get current location on mount (non-blocking)
        getCurrentLocation();

        // Listen to camera idle event to update location when map stops moving
        map.addListener('idle', () => {
          const center = map.getCenter();
          if (center) {
            const location = {
              lat: center.lat(),
              lng: center.lng(),
            };
            setSelectedLocation(location);
            setCurrentZoom(map.getZoom());
            reverseGeocodeLocation(location);
          }
        });

        // Listen to zoom change
        map.addListener('zoom_changed', () => {
          setCurrentZoom(map.getZoom());
        });
      }
    });
  }, [isOpen, loadGoogleMapsScript, getCurrentLocation, reverseGeocodeLocation]);

  // Handle search
  const handleSearch = useCallback((query) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    if (!autocompleteServiceRef.current) return;

    setIsSearching(true);
    autocompleteServiceRef.current.getPlacePredictions(
      {
        input: query,
        types: ['geocode'],
      },
      (predictions, status) => {
        setIsSearching(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSearchResults(predictions);
          setShowSearchResults(true);
        } else {
          setSearchResults([]);
          setShowSearchResults(false);
        }
      }
    );
  }, []);

  // Select search result
  const selectSearchResult = useCallback((placeId) => {
    if (!placesServiceRef.current) return;

    placesServiceRef.current.getDetails(
      {
        placeId: placeId,
        fields: ['geometry', 'formatted_address', 'name'],
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const location = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          setSelectedLocation(location);
          setCurrentAddress(place.formatted_address || place.name || '');
          setSearchQuery('');
          setShowSearchResults(false);
          setIsSearchFocused(false);
          if (googleMapInstanceRef.current) {
            googleMapInstanceRef.current.panTo(place.geometry.location);
            googleMapInstanceRef.current.setZoom(15);
          }
        }
      }
    );
  }, []);

  // Handle confirm
  const handleConfirm = () => {
    if (currentAddress) {
      onConfirm({
        location: selectedLocation,
        address: currentAddress,
        zoom: currentZoom,
        coordinates: {
          latitude: selectedLocation.lat,
          longitude: selectedLocation.lng,
        },
      });
      handleClose();
    }
  };

  // Handle close
  const handleClose = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    onClose();
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchResults(false);
    setIsSearchFocused(false);
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full h-full flex flex-col bg-white overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-green-600 relative z-40 flex-shrink-0">
          <h2 className={typography.h2} style={{ color: '#ffffff' }}>
            Pick Location
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={getCurrentLocation}
              className="p-2 rounded-lg hover:bg-green-700 transition-colors"
              title="Use Current Location"
            >
              <Navigation className="w-5 h-5 text-white" />
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg hover:bg-green-700 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Map Container */}
        <div className="flex-1 relative overflow-hidden">
          <div ref={mapRef} className="w-full h-full" />
          
          {/* Loading Overlay */}
          {isLoadingLocation && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-50 z-10">
              <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
            </div>
          )}
          
          {/* Center Marker */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center z-20">
            <MapPin className="w-12 h-12 text-red-600" style={{ transform: 'translateY(-50%)' }} />
          </div>

          {/* Search Bar */}
          <div className="absolute top-4 left-4 right-4 z-30 pointer-events-auto">
                <div className="relative">
                  <div className="bg-white rounded-lg shadow-lg">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchQuery}
                        onChange={(e) => {
                          setSearchQuery(e.target.value);
                          handleSearch(e.target.value);
                        }}
                        onFocus={() => {
                          setIsSearchFocused(true);
                          if (searchResults.length > 0) {
                            setShowSearchResults(true);
                          }
                        }}
                        onBlur={() => {
                          // Delay to allow click on search results
                          setTimeout(() => {
                            setIsSearchFocused(false);
                          }, 200);
                        }}
                        placeholder="Search for a location..."
                        className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                      {searchQuery && (
                        <button
                          onClick={clearSearch}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2"
                        >
                          <X className="w-5 h-5 text-gray-400" />
                        </button>
                      )}
                      {isSearching && !searchQuery && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                        </div>
                      )}
                    </div>

                    {/* Search Results Dropdown */}
                    {showSearchResults && searchResults.length > 0 && (
                      <div className="mt-1 max-h-60 overflow-y-auto border-t border-gray-200">
                        {searchResults.map((prediction) => (
                          <button
                            key={prediction.place_id}
                            onClick={() => selectSearchResult(prediction.place_id)}
                            className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-start gap-3">
                              <MapPin className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-gray-900 truncate">
                                  {prediction.structured_formatting?.main_text || prediction.description}
                                </p>
                                {prediction.structured_formatting?.secondary_text && (
                                  <p className="text-sm text-gray-500 truncate">
                                    {prediction.structured_formatting.secondary_text}
                                  </p>
                                )}
                              </div>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Instructions Overlay */}
                {!isSearching && !showSearchResults && !isSearchFocused && (
                  <div className="mt-2 px-3 py-2 bg-black bg-opacity-75 rounded-lg">
                    <p className="text-sm text-center" style={{ color: '#ffffff' }}>
                      Move the map to center the pin on your desired location
                    </p>
                  </div>
                 )}
               </div>
         </div>

        {/* Location Details Panel */}
        <div className="p-4 border-t bg-white shadow-lg relative z-40 flex-shrink-0">
          <div className="space-y-4">
            <div>
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '0.5rem' }}>
                Selected Location
              </h3>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500 mb-1">
                    Coordinates: {selectedLocation.lat.toFixed(6)}, {selectedLocation.lng.toFixed(6)}
                  </p>
                  <div className="h-5 overflow-hidden">
                    {isLoadingAddress ? (
                      <div className="flex items-center gap-2 h-5">
                        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
                        <span className="text-sm text-gray-500">Loading address...</span>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-800 h-5 overflow-hidden text-ellipsis">
                        {currentAddress || 'Loading address...'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <PrimaryButton
              onClick={handleConfirm}
              disabled={!currentAddress || isLoadingAddress}
              className="w-full"
            >
              Confirm Location
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default HaltPicker;

